import type { BasketItem } from '../page';

interface OrderDiscount {
  type: 'percentage' | 'fixed';
  value: number;
  coupon?: {
    code: string;
    name?: string;
    discountAmount: number;
  };
}

interface TotalsPanelProps {
  basketItems: BasketItem[];
  orderDiscount: OrderDiscount | null;
  onRemoveDiscount: () => void;
}

const toRateNumber = (value: unknown): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const parseDateSafe = (value: unknown): Date | null => {
  if (!value) return null;
  const date = new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
};

const getActiveItemDiscount = (item: BasketItem) => {
  const now = new Date();
  const discounts = Array.isArray(item?.discounts) ? item.discounts : [];
  return (
    discounts.find((discount) => {
      const from = parseDateSafe(
        discount?.effectiveFrom ??
          discount?.effectiveFromDate ??
          discount?.startDate ??
          discount?.fromDate ??
          discount?.validFrom
      );
      const to = parseDateSafe(
        discount?.effectiveTo ??
          discount?.effectiveToDate ??
          discount?.endDate ??
          discount?.toDate ??
          discount?.validTo
      );
      if (from && now < from) return false;
      if (to && now > to) return false;
      return true;
    }) ?? null
  );
};

const getItemDiscountAmount = (item: BasketItem): number => {
  const activeDiscount = getActiveItemDiscount(item);
  if (!activeDiscount) return 0;

  const lineBase = Number(item.price || 0) * Number(item.qty || 0);
  if (lineBase <= 0) return 0;

  const typeName = String(
    activeDiscount?.discountType?.name ??
      activeDiscount?.discountTypeName ??
      activeDiscount?.amountType?.name ??
      activeDiscount?.amountTypeName ??
      activeDiscount?.type ??
      activeDiscount?.valueType ??
      ''
  ).toLowerCase();
  const typeId = Number(
    activeDiscount?.discountType?.id ??
      activeDiscount?.discountTypeId ??
      activeDiscount?.amountType?.id ??
      activeDiscount?.amountTypeId ??
      -1
  );
  const isPercentage =
    activeDiscount?.isPercentage === true ||
    typeId === 1 ||
    typeName.includes('percent');

  const discountValue = toRateNumber(
    activeDiscount?.percentage ??
      activeDiscount?.percent ??
      activeDiscount?.rate ??
      activeDiscount?.discountPercentage ??
      activeDiscount?.amount ??
      activeDiscount?.discountAmount ??
      activeDiscount?.value
  );
  if (discountValue <= 0) return 0;

  let discountAmount = isPercentage
    ? lineBase * (discountValue / 100)
    : discountValue * Number(item.qty || 0);

  const maxValue = toRateNumber(
    activeDiscount?.maximumValue ??
      activeDiscount?.maximumDiscount ??
      activeDiscount?.maxDiscount ??
      activeDiscount?.maxValue ??
      activeDiscount?.maxAmount ??
      activeDiscount?.maximumAmount
  );
  if (maxValue > 0) {
    discountAmount = Math.min(discountAmount, maxValue);
  }

  return Math.max(0, Math.min(discountAmount, lineBase));
};

const getItemVatRate = (item: BasketItem): number => {
  const taxes = Array.isArray(item?.taxes) ? item.taxes : [];
  const extractedRate = taxes.reduce((sum, tax) => {
    const rate = toRateNumber(
      tax?.rate ??
      tax?.taxRate ??
      tax?.percentage ??
      tax?.percent ??
      tax?.amount ??
      tax?.vatPercentage ??
      tax?.value ??
      tax?.tax?.rate ??
      tax?.tax?.amount
    );
    return sum + Math.max(0, rate);
  }, 0);
  if (extractedRate > 0) return extractedRate;
  return 0;
};

export default function TotalsPanel({ basketItems, orderDiscount, onRemoveDiscount }: TotalsPanelProps) {
  // Gross amount before any discounts (price × qty)
  const amountBeforeDiscount = basketItems.reduce((sum, item) => sum + item.total, 0);

  // Line item (product-level) discounts
  const lineItemDiscount = basketItems.reduce((sum, item) => sum + getItemDiscountAmount(item), 0);

  // Subtotal = gross minus line item discounts
  const subtotal = amountBeforeDiscount - lineItemDiscount;

  // VAT is fixed on subtotal (not affected by order discount)
  const vatAmount = basketItems
    .filter(item => getItemVatRate(item) > 0)
    .reduce((sum, item) => {
      const itemSubtotal = item.total;
      const itemAfterLineDiscount = amountBeforeDiscount > 0
        ? itemSubtotal * (subtotal / amountBeforeDiscount)
        : 0;
      return sum + (itemAfterLineDiscount * (getItemVatRate(item) / 100));
    }, 0);

  // Order discount with coupon/discount interaction rules:
  // Case 1: if manual is 100%, coupon is blocked (handled in modal)
  // Case 2 (fixed): fixed applies first, coupon fills remaining gap
  // Case 3 (percentage): coupon applied first, percentage applies to (subtotal - coupon)
  let orderDiscountAmount = 0;
  let effectiveCouponAmount = 0;
  if (orderDiscount) {
    const rawCoupon = orderDiscount.coupon?.discountAmount ?? 0;
    if (orderDiscount.type === 'percentage') {
      const manualBase = Math.max(0, subtotal - rawCoupon);
      const manualAmount = manualBase * (orderDiscount.value / 100);
      effectiveCouponAmount = rawCoupon;
      orderDiscountAmount = manualAmount + effectiveCouponAmount;
    } else {
      const fixedAmount = Math.min(orderDiscount.value, subtotal);
      effectiveCouponAmount = Math.max(0, Math.min(rawCoupon, subtotal - fixedAmount));
      orderDiscountAmount = fixedAmount + effectiveCouponAmount;
    }
    orderDiscountAmount = Math.min(orderDiscountAmount, subtotal);
  }

  // Bag fee and rounding are temporarily disabled.
  const grandTotal = subtotal + vatAmount - orderDiscountAmount;

  return (
    <div className="bg-white border-b border-l border-gray-200 p-4">
      <h2 className="text-sm font-bold text-gray-900 mb-4 whitespace-nowrap">Transaction Summary</h2>

      <div className="space-y-2.5 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 whitespace-nowrap">Amount Before Discount</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{amountBeforeDiscount.toFixed(2)}</span>
        </div>

        {lineItemDiscount > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-sm text-orange-600 whitespace-nowrap">Line Item Discount</span>
            <span className="text-sm font-semibold text-orange-600 whitespace-nowrap">-{lineItemDiscount.toFixed(2)}</span>
          </div>
        )}

        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 whitespace-nowrap">Subtotal</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{subtotal.toFixed(2)}</span>
        </div>

        {orderDiscount && orderDiscountAmount > 0 && (
          <div className="flex items-center justify-between group">
            <div className="flex items-center gap-2 min-w-0">
              <span className="text-sm font-semibold text-orange-600 whitespace-nowrap">
                Discount: {orderDiscountAmount.toFixed(2)}
                {orderDiscount.coupon && effectiveCouponAmount > 0 && (
                  <span className="font-normal text-orange-400"> ( Coupon {effectiveCouponAmount.toFixed(2)} )</span>
                )}
              </span>
              <button
                onClick={onRemoveDiscount}
                className="opacity-0 group-hover:opacity-100 transition-opacity cursor-pointer flex-shrink-0"
                title="Remove discount"
              >
                <i className="ri-close-circle-fill text-red-500 text-base"></i>
              </button>
            </div>
          </div>
        )}
        
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-600 whitespace-nowrap">Total VAT</span>
          <span className="text-sm font-semibold text-gray-900 whitespace-nowrap">{vatAmount.toFixed(2)}</span>
        </div>
        
      </div>

      <div className="pt-4 border-t-2 border-gray-300 mb-4">
        <div className="flex items-center justify-between">
          <span className="text-lg font-bold text-gray-900 whitespace-nowrap">Grand Total</span>
          <span className="text-3xl font-bold text-teal-600 whitespace-nowrap">{grandTotal.toFixed(2)}</span>
        </div>
      </div>

    </div>
  );
}

